import { createFileRoute } from "@tanstack/react-router";
import { AppShell } from "@/components/layout/app-shell";
import { ProtectedRoute } from "@/components/layout/protected-route";
import { useAuth } from "@/hooks/use-auth";
import { AnalyticsStats } from "@/components/analytics/analytics-stats";
import { RevenueChart } from "@/components/analytics/revenue-chart";
import { TransactionVolumeChart } from "@/components/analytics/transaction-volume-chart";
import { TopMerchantsTable } from "@/components/analytics/top-merchants-table";
import { ChannelAnalyticsChart } from "@/components/analytics/channel-analytics-chart";
import { SystemAnalyticsOverview } from "@/components/analytics/system-analytics-overview";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { IconCalendar } from "@tabler/icons-react";
import { useState } from "react";
import { format, subDays } from "date-fns";
import type { AnalyticsParams as HookAnalyticsParams } from "@/hooks/use-analytics";
import type { AnalyticsParams as APIAnalyticsParams } from "@/types/api";

export const Route = createFileRoute("/analytics/")({
  component: AnalyticsPage,
});

function AnalyticsPage() {
  return (
    <ProtectedRoute>
      <AdminOnlyRoute>
        <AppShell>
          <AnalyticsContent />
        </AppShell>
      </AdminOnlyRoute>
    </ProtectedRoute>
  );
}

// Component to restrict access to admins only
function AdminOnlyRoute({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();

  if (user?.role !== 'admin') {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-6xl mb-4">🚫</div>
          <h2 className="text-xl font-semibold text-destructive mb-2">Access Denied</h2>
          <p className="text-muted-foreground">
            This feature is only available to administrators.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

function AnalyticsContent() {
  const [period, setPeriod] = useState<"7d" | "30d" | "90d" | "1y">("30d");
    const [startDate, setStartDate] = useState<Date | undefined>(
    subDays(new Date(), 30)
  );
  const [endDate, setEndDate] = useState<Date | undefined>(new Date());
  const [_customDateRange, setCustomDateRange] = useState(false);

  // Params for hook-based analytics (period-based)
  const hookParams: HookAnalyticsParams = { period };

  // Params for API-based analytics (date range)
  const apiParams: APIAnalyticsParams = {
    date_from: startDate ? format(startDate, "yyyy-MM-dd") : "",
    date_to: endDate ? format(endDate, "yyyy-MM-dd") : "",
  };

  const handlePeriodChange = (value: string) => {
    setPeriod(value as "7d" | "30d" | "90d" | "1y");
    setCustomDateRange(false);

    // Update date range based on period
    const today = new Date();
    setEndDate(today);
    switch (value) {
      case "7d":
        setStartDate(subDays(today, 7));
        break;
      case "30d":
        setStartDate(subDays(today, 30));
        break;
      case "90d":
        setStartDate(subDays(today, 90));
        break;
      case "1y":
        setStartDate(subDays(today, 365));
        break;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Analytics</h1>
          <p className="text-muted-foreground">
            Business insights and performance metrics
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={period} onValueChange={handlePeriodChange}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Select period" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">Last 7 Days</SelectItem>
              <SelectItem value="30d">Last 30 Days</SelectItem>
              <SelectItem value="90d">Last 90 Days</SelectItem>
              <SelectItem value="1y">Last Year</SelectItem>
            </SelectContent>
          </Select>

          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline">
                <IconCalendar className="mr-2 h-4 w-4" />
                Custom Range
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <div className="p-3 space-y-2">
                <div className="font-medium text-sm">Date Range</div>
                <div className="flex gap-2">
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">
                      From
                    </div>
                    <Calendar
                      mode="single"
                      selected={startDate}
                      onSelect={(date) => {
                        setStartDate(date);
                        setCustomDateRange(true);
                      }}
                      initialFocus
                    />
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">
                      To
                    </div>
                    <Calendar
                      mode="single"
                      selected={endDate}
                      onSelect={(date) => {
                        setEndDate(date);
                        setCustomDateRange(true);
                      }}
                      initialFocus
                    />
                  </div>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      <AnalyticsStats />

      {/* System Overview */}
      <SystemAnalyticsOverview params={apiParams} />

      {/* Charts */}
      <div className="grid gap-6 md:grid-cols-1 lg:grid-cols-2">
        <RevenueChart params={hookParams} />
        <TransactionVolumeChart params={hookParams} />
      </div>

      {/* Channel Analytics */}
      <ChannelAnalyticsChart params={apiParams} />

      {/* Top Merchants */}
      <TopMerchantsTable limit={10} />
    </div>
  );
}
